import React, { useState, useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { Button, Chip, Dialog, DialogContent, DialogActions, DialogTitle, IconButton, Tooltip, Link, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LaunchIcon from '@mui/icons-material/Launch';
import { arrayOf, shape, string, number, objectOf, oneOfType } from 'prop-types';
import DeviceIcon from '#component/icon/device-icon';
import DevicePopup from '#component/device/devices-popup/device-popup';

const RootBox = styled(Box)({
  textAlign: 'left'
});

const DeviceChipContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 4
});

const StyledChip = styled(Chip)({
  maxWidth: '100%',
  border: 0
});

const StyledDeviceIcon = styled(DeviceIcon)(({ theme }) => ({
  marginLeft: `${theme.spacing(0.75)} !important`
}));

const StyledMoreVertIcon = styled(MoreVertIcon)({
  position: 'absolute',
  top: '33%',
  right: '-18px',
  '&:hover': {
    fill: '#5eb676'
  }
});

const StyledButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    marginBottom: theme.spacing(1)
  }
}));

const StyledDialogTitle = styled(DialogTitle)({
  padding: '8px 22px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
});

const StyledDialogActions = styled(DialogActions)({
  paddingRight: '22px'
});

const DeviceChip = ({ device, meta, chipClassName, chipStyle }) => (
  <DeviceChipContainer>
    <StyledChip
      key={`${device.id} + ${device.projectId || 1}`}
      className={chipClassName}
      style={chipStyle}
      variant="outlined"
      label={device.label || device.title || device.uid}
      icon={<StyledDeviceIcon
        icon={meta.device?.find(m => m.name === device.type)?.icon}
      />}
    />
    <Tooltip title="Open the device">
      <Link
        href={`${window.location.origin}/device/${device.id}`}
        color="inherit"
        target="_blank"
        onMouseDown={e => e.stopPropagation()}
      >
        <IconButton onClick={e => e.stopPropagation()} size="small">
          <LaunchIcon
            fontSize="small"
            color="action"
          />
        </IconButton>
      </Link>
    </Tooltip>
  </DeviceChipContainer>
);

/**
 * Renders clickable devices chips with title.
 * @returns {React.FC}
 */

const DevicesChips = ({
  devices = [],
  classes = {},
  styles = {},
  isProject,
  project = {},
  openInPopup,
  user,
  isShipment
}) => {
  const [showAll, setShowAll] = useState(false);
  const [showDialog, setDialog] = useState(false);
  const [showDevicesPopup, setDevicesPopup] = useState(false);
  const meta = useSelector(s => s.meta, shallowEqual);
  const maxDisplayedChips = isShipment ? 2 : 10;

  const toggleShowAll = () => {
    setShowAll(!showAll);
  };

  const toggleDialog = e => {
    e.stopPropagation();
    setDialog(!showDialog);
  };

  const toggleDevicesPopup = () => setDevicesPopup(!showDevicesPopup);
  const projectId = useMemo(() => ({ id: project.id }), [project.id]);

  return (
    <>
      <RootBox className={classes.root} style={styles.root}>
        {devices.slice(0, showAll ? undefined : maxDisplayedChips).map(d => (
          <DeviceChip
            device={d}
            meta={meta}
            key={d.id}
            chipClassName={classes.shipmentChip}
            chipStyle={styles.shipmentChip}
          />
        ))}
        {devices.length > maxDisplayedChips && (
          isShipment ?
            <StyledMoreVertIcon
              onClick={toggleDialog}
            /> :
            <>
              <br />
              <StyledButton
                onClick={(isProject || openInPopup) ? toggleDevicesPopup : toggleShowAll}
              >
                {!showAll ? `Show all (${devices.length})` : 'Show less'}
              </StyledButton>
            </>
        )}
      </RootBox>
      <Dialog
        open={showDialog}
        onClose={toggleDialog}
      >
        <StyledDialogTitle>
          Devices
          <IconButton
            aria-label="close"
            onClick={toggleDialog}
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <DialogContent>
          {devices.map(d => (
            <DeviceChip
              device={d}
              meta={meta}
              key={d.id}
              chipClassName={classes.shipmentChip}
              chipStyle={styles.shipmentChip}
            />
          ))}
        </DialogContent>
        <StyledDialogActions>
          <Button
            onClick={toggleDialog}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
        </StyledDialogActions>
      </Dialog>
      {showDevicesPopup &&
        <DevicePopup
          project={projectId}
          user={user}
          open={showDevicesPopup}
          handleClose={toggleDevicesPopup}
        />}
    </>
  );
};

DevicesChips.propTypes = {
  devices: arrayOf(shape({
    id: number,
    projectId: number,
    uid: string,
    title: string,
    type: string
  })),
  classes: objectOf(string),
  styles: objectOf(oneOfType([string, number, objectOf(oneOfType([string, number]))]))
};

export default DevicesChips;
